import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  SafeAreaView,
  StatusBar,
  TouchableOpacity,
  Image,
  ActivityIndicator,
  Alert,
  Linking,
  Platform,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Bold from '../../../typography/BoldText';
import Medium from '../../../typography/MediumText';
import Regular from '../../../typography/RegularText';
import colors from '../../../util/colors';
import {mvs} from '../../../util/metrices';
import ApiService from '../../../util/apiService';
import Icon from 'react-native-vector-icons/Ionicons';

const DEVOTION_RADIUS = 20;

const AuthorBio = ({navigation, route}) => {
  const [authorData, setAuthorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { authorId, authorName } = route.params || {};

  useEffect(() => {
    loadAuthorData();
  }, [authorId]);

  const loadAuthorData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading author data for ID:', authorId, 'Name:', authorName);
      
      if (authorId) {
        const author = await ApiService.getAuthorProfile(authorId);
        console.log('Author data received:', author);
        setAuthorData(author);
      } else {
        console.log('No author ID provided, using fallback data');
        // Use fallback data if no author ID provided
        setAuthorData({
          full_name: authorName || 'Unknown Author',
          bio: 'Author information not available.',
          username: '',
          links: []
        });
      }
    } catch (err) {
      console.error('Failed to load author data:', err);
      setError(err.message);
      // Set fallback data
      setAuthorData({
        full_name: authorName || 'Unknown Author',
        bio: 'Unable to load author information at this time.',
        username: '',
        links: []
      });
    } finally {
      setLoading(false);
    }
  };

  const openLink = (url) => {
    Linking.openURL(url).catch(err => {
      console.error('Failed to open link:', err);
      Alert.alert('Error', 'Could not open link');
    });
  };

  // Legacy fallback data structure for display
  const authorInfo = {
    name: authorData?.full_name || authorName || 'Unknown Author',
    photo: authorData?.profile_image ? { uri: authorData.profile_image } : require('../../../assets/img/logo.png'),
    bio: authorData?.bio || 'Author information not available.',
    credentials: '', // Not stored in current DB schema
    ministry: '', // Not stored in current DB schema  
    location: authorData?.location || '',
    email: authorData?.email || '', // Email may be filtered by privacy settings
    phone: authorData?.phone || '', // Phone may be filtered by privacy settings
    website: authorData?.website || '',
    links: Array.isArray(authorData?.links) ? authorData.links : [],
    achievements: [], // Not stored in current DB schema
  };

  console.log('AuthorInfo object created:', {
    name: authorInfo.name,
    linksCount: authorInfo.links?.length || 0,
    hasData: !!authorData
  });

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor={colors.primary} />
      {/* Header */}
      <LinearGradient
        colors={[colors.primary, colors.secondary]}
        style={styles.header}
      >
        <View style={styles.headerContent}>
          <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
            <Icon name="arrow-back" size={24} color={colors.textLight} />
          </TouchableOpacity>
          <Bold style={styles.headerTitle}>About the Author</Bold>
          <View style={styles.placeholder} />
        </View>
      </LinearGradient>
      <ScrollView contentContainerStyle={styles.scrollContainer}>
        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={colors.primary} />
            <Regular style={styles.loadingText}>Loading author information...</Regular>
          </View>
        ) : (
          <>
            {/* Author Profile */}
            <View style={styles.profileSection}>
              <Image source={authorInfo.photo} style={styles.authorPhoto} />
              <Bold style={styles.authorName}>{authorInfo.name}</Bold>
            </View>
            {/* Bio */}
            {authorInfo.bio && (
              <View style={styles.bioSection}>
                <Bold style={styles.sectionTitle}>Biography</Bold>
                <Regular style={styles.bioText}>{authorInfo.bio}</Regular>
              </View>
            )}
            {/* Contact Information */}
            {(authorInfo.location || authorInfo.website || authorInfo.phone || authorInfo.email) && (
              <View style={styles.contactSection}>
                <Bold style={styles.sectionTitle}>Contact Information</Bold>
                {authorInfo.location && (
                  <View style={styles.contactItem}>
                    <Icon name="location-outline" size={20} color={colors.primary} />
                    <Regular style={styles.contactText}>{authorInfo.location}</Regular>
                  </View>
                )}
                {authorInfo.phone && (
                  <TouchableOpacity 
                    style={styles.contactItem}
                    onPress={() => openLink(`tel:${authorInfo.phone}`)}
                  >
                    <Icon name="call-outline" size={20} color={colors.primary} />
                    <Regular style={[styles.contactText, styles.linkText]}>{authorInfo.phone}</Regular>
                  </TouchableOpacity>
                )}
                {authorInfo.email && (
                  <TouchableOpacity 
                    style={styles.contactItem}
                    onPress={() => openLink(`mailto:${authorInfo.email}`)}
                  >
                    <Icon name="mail-outline" size={20} color={colors.primary} />
                    <Regular style={[styles.contactText, styles.linkText]}>{authorInfo.email}</Regular>
                  </TouchableOpacity>
                )}
                {authorInfo.website && (
                  <TouchableOpacity 
                    style={styles.contactItem}
                    onPress={() => openLink(authorInfo.website)}
                  >
                    <Icon name="globe-outline" size={20} color={colors.primary} />
                    <Regular style={[styles.contactText, styles.linkText]}>{authorInfo.website}</Regular>
                  </TouchableOpacity>
                )}
              </View>
            )}
            {/* Social Links */}
            {authorInfo.links && Array.isArray(authorInfo.links) && authorInfo.links.length > 0 && (
              <View style={styles.linksSection}>
                <Bold style={styles.sectionTitle}>Social Links</Bold>
                {authorInfo.links.map((link, index) => (
                  link && link.label && link.url ? (
                    <TouchableOpacity 
                      key={index} 
                      style={styles.linkItem}
                      onPress={() => openLink(link.url)}
                    >
                      <Icon name="link-outline" size={20} color={colors.primary} />
                      <View style={styles.linkContent}>
                        <Medium style={styles.linkLabel}>{link.label}</Medium>
                        <Regular style={styles.linkUrl}>{link.url}</Regular>
                      </View>
                      <Icon name="chevron-forward" size={16} color={colors.textSecondary} />
                    </TouchableOpacity>
                  ) : null
                ))}
              </View>
            )}
            {error && (
              <View style={styles.errorContainer}>
                <Regular style={styles.errorText}>
                  Note: Some author information may not be available at this time.
                </Regular>
              </View>
            )}
            {/* Back to Devotion Button */}
            <TouchableOpacity style={styles.backToLoginButton} onPress={() => navigation.goBack()}>
              <LinearGradient
                colors={[colors.primary, colors.secondary]}
                style={styles.backToLoginGradient}
              >
                <Icon name="arrow-back" size={20} color={colors.textLight} />
                <Bold style={styles.backToLoginText}>Back to Devotion</Bold>
              </LinearGradient>
            </TouchableOpacity>
          </>
        )}
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  header: {
    paddingTop: mvs(10),
    paddingBottom: mvs(20),
    paddingHorizontal: mvs(20),
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  backButton: {
    padding: mvs(8),
  },
  headerTitle: {
    fontSize: mvs(20),
    color: colors.textLight,
  },
  placeholder: {
    width: mvs(40),
  },
  scrollContainer: {
    paddingHorizontal: mvs(20),
    paddingBottom: mvs(30),
  },
  profileSection: {
    alignItems: 'center',
    marginTop: mvs(20),
    marginBottom: mvs(30),
  },
  authorPhoto: {
    width: mvs(120),
    height: mvs(120),
    borderRadius: mvs(60),
    marginBottom: mvs(16),
  },
  authorName: {
    fontSize: mvs(24),
    color: colors.text,
    marginBottom: mvs(8),
  },
  authorCredentials: {
    fontSize: mvs(16),
    color: colors.primary,
    marginBottom: mvs(4),
  },
  authorMinistry: {
    fontSize: mvs(14),
    color: colors.textSecondary,
  },
  bioSection: {
    marginBottom: mvs(30),
  },
  sectionTitle: {
    fontSize: mvs(18),
    color: colors.text,
    marginBottom: mvs(16),
  },
  bioText: {
    fontSize: mvs(16),
    color: colors.text,
    lineHeight: mvs(24),
  },
  contactSection: {
    marginBottom: mvs(30),
  },
  contactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: mvs(12),
  },
  contactText: {
    fontSize: mvs(14),
    color: colors.text,
    marginLeft: mvs(12),
  },
  expertiseSection: {
    marginBottom: mvs(30),
  },
  expertiseList: {
    backgroundColor: colors.surface,
    borderRadius: mvs(12),
    padding: mvs(16),
  },
  expertiseItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: mvs(8),
  },
  expertiseText: {
    fontSize: mvs(14),
    color: colors.text,
    marginLeft: mvs(8),
  },
  achievementsSection: {
    marginBottom: mvs(30),
  },
  achievementsList: {
    backgroundColor: colors.surface,
    borderRadius: mvs(12),
    padding: mvs(16),
  },
  achievementItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: mvs(12),
  },
  achievementText: {
    fontSize: mvs(14),
    color: colors.text,
    marginLeft: mvs(8),
    flex: 1,
    lineHeight: mvs(20),
  },
  backToLoginButton: {
    borderRadius: mvs(12),
    shadowColor: colors.primary,
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 6,
  },
  backToLoginGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: mvs(16),
    paddingHorizontal: mvs(20),
    borderRadius: mvs(12),
  },
  backToLoginText: {
    fontSize: mvs(16),
    color: colors.textLight,
    marginLeft: mvs(8),
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: mvs(50),
  },
  loadingText: {
    fontSize: mvs(16),
    color: colors.textSecondary,
    marginTop: mvs(16),
  },
  errorContainer: {
    backgroundColor: colors.accent,
    borderRadius: mvs(8),
    padding: mvs(12),
    marginVertical: mvs(16),
  },
  errorText: {
    fontSize: mvs(14),
    color: colors.textSecondary,
    textAlign: 'center',
  },
  linkText: {
    color: colors.primary,
    textDecorationLine: 'underline',
  },
  linksSection: {
    marginBottom: mvs(30),
  },
  linkItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRadius: mvs(12),
    padding: mvs(16),
    marginBottom: mvs(8),
  },
  linkContent: {
    flex: 1,
    marginLeft: mvs(12),
  },
  linkLabel: {
    fontSize: mvs(16),
    color: colors.text,
    marginBottom: mvs(2),
  },
  linkUrl: {
    fontSize: mvs(14),
    color: colors.textSecondary,
  },
});

export default AuthorBio;